import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminScreen, LoadOrError, adminErrorText, subscriptionTone, useResource } from './Overview';
import {
  Alert, Button, Card, EmptyState, Icon, Input, Modal, Spinner, StatusPill, Textarea, useToast
} from '../../components/ui';
import { api } from '../../lib/api';
import { subscriptionLabel, datePt } from '../../lib/format';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

interface AdminBusinessRow {
  id: string;
  name: string;
  slug: string;
  status: 'active' | 'suspended';
  isPublic: number;
  createdAt: string;
  ownerEmail: string;
  subscriptionStatus: string | null;
  planName: string | null;
}

interface BusinessesPayload {
  businesses: AdminBusinessRow[];
}

type PendingAction = { business: AdminBusinessRow; kind: 'suspend' | 'reactivate' | 'removeLicense' };

export function AdminBusinessesPage(): JSX.Element {
  usePageMeta(tr('Empresas — administração'), tr('Estabelecimentos cadastrados, situação e vínculo com assinaturas.'), '/admin/empresas');

  const toast = useToast();
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const resource = useResource<BusinessesPayload>(
    () => api.get<BusinessesPayload>(`/api/admin/businesses?q=${encodeURIComponent(query)}`),
    [query]
  );

  const rows = resource.data?.businesses ?? [];
  const suspended = rows.filter((r) => r.status === 'suspended').length;

  async function runAction(): Promise<void> {
    if (!pending) return;
    if (pending.kind === 'suspend' && reason.trim().length < 3) {
      setFormError(tr('O motivo da suspensão é obrigatório (mínimo de 3 caracteres) — ele fica registrado na auditoria.'));
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      if (pending.kind === 'suspend') {
        await api.post(`/api/admin/businesses/${pending.business.id}/suspend`, { reason: reason.trim() });
        toast(tr('{name} foi suspenso. A assinatura também foi suspensa.', { name: pending.business.name }), 'success');
      } else if (pending.kind === 'removeLicense') {
        const res = await api.del<{ ok: boolean; removed: { businessName: string } }>(
          `/api/admin/businesses/${pending.business.id}/license`,
          { reason: reason.trim() || undefined }
        );
        toast(tr('Licença de {name} removida. O estabelecimento está sem plano.', { name: res.removed.businessName }), 'success');
      } else {
        const res = await api.post<{ ok: boolean; message: string }>(`/api/admin/businesses/${pending.business.id}/reactivate`);
        toast(res.message || tr('Estabelecimento reativado.'), 'success');
      }
      setPending(null);
      setReason('');
      await resource.reload();
    } catch (e) {
      setFormError(adminErrorText(e));
      toast(adminErrorText(e), 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminScreen
      title={tr("Estabelecimentos")}
      description={tr("Todos os salões e clínicas da plataforma, com dono, plano e situação da assinatura. Suspender bloqueia o acesso operacional e a assinatura junto. Excluir licença deixa o estabelecimento sem plano sem apagar a conta nem o histórico de cobranças.")}
    >
      <form
        className="mb-4 flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(term.trim());
        }}
      >
        <div className="min-w-[16rem] flex-1">
          <label className="nl-label" htmlFor="admin-biz-search">{tr("Buscar estabelecimento")}</label>
          <Input
            id="admin-biz-search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={tr("Nome do negócio ou e-mail do dono")}
            maxLength={80}
          />
        </div>
        <Button type="submit" variant="secondary" disabled={resource.loading}>
          <Icon name="search" /> {tr("Buscar")}
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={resource.loading}
          onClick={() => {
            setTerm('');
            setQuery('');
            void resource.reload();
          }}
        >
          {tr("Limpar")}
        </Button>
        <Button type="button" variant="ghost" disabled={resource.loading} onClick={() => void resource.reload()}>
          {resource.loading ? <Spinner label="" /> : <Icon name="chart" />} {tr("Atualizar")}
        </Button>
      </form>

      <LoadOrError loading={resource.loading} error={resource.error} />

      {rows.length > 0 ? (
        <p className="nl-hint mb-3">
          {rows.length} {rows.length === 1 ? 'estabelecimento listado' : 'estabelecimentos listados'} · {suspended} {tr("suspenso(s).")}
          <span className="ml-1">{tr("A listagem do backend traz no máximo 200 registros.")}</span>
        </p>
      ) : null}

      {!resource.loading && !resource.error && rows.length === 0 ? (
        <EmptyState
          title={tr("Nenhum estabelecimento encontrado")}
          description={tr("Ajuste a busca ou cadastre o primeiro estabelecimento pelo fluxo de início profissional.")}
        />
      ) : null}

      <div className="grid gap-3">
        {rows.map((business) => (
          <Card key={business.id} as="article">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-xl">{business.name}</h2>
                <p className="nl-hint">{tr("Dono:")} {business.ownerEmail} {tr("· Endereço público: /")}{business.slug} {tr("· Criado em")} {datePt(business.createdAt)}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {business.status === 'active' ? (
                    <StatusPill tone="positive">{tr("Operação ativa")}</StatusPill>
                  ) : (
                    <StatusPill tone="danger">{tr("Suspenso pela plataforma")}</StatusPill>
                  )}
                  {business.isPublic ? (
                    <StatusPill tone="positive">{tr("Perfil público publicado")}</StatusPill>
                  ) : (
                    <StatusPill tone="neutral">{tr("Perfil público oculto")}</StatusPill>
                  )}
                  <StatusPill tone={subscriptionTone(business.subscriptionStatus)}>
                    {tr("Assinatura:")} {business.subscriptionStatus
                      ? `${subscriptionLabel(business.subscriptionStatus)}${business.planName ? ` — ${business.planName}` : ''}`
                      : tr('Sem assinatura registrada')}
                  </StatusPill>
                </div>
              </div>
              <div className="flex flex-col items-stretch gap-2">
                <Link
                  to={`/admin/assinaturas?empresa=${encodeURIComponent(business.id)}`}
                  className="nl-btn nl-btn-secondary"
                >
                  {tr("Assinatura e cobranças")} <Icon name="chevron" size={16} />
                </Link>
                {business.subscriptionStatus ? (
                  <Button
                    variant="secondary"
                    disabled={resource.loading}
                    onClick={() => {
                      setFormError(null);
                      setReason('');
                      setPending({ business, kind: 'removeLicense' });
                    }}
                  >
                    {tr("Excluir licença")}
                  </Button>
                ) : null}
                {business.status === 'active' ? (
                  <Button
                    variant="danger"
                    disabled={resource.loading}
                    onClick={() => {
                      setFormError(null);
                      setReason('');
                      setPending({ business, kind: 'suspend' });
                    }}
                  >
                    {tr("Suspender")}
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    disabled={resource.loading}
                    onClick={() => {
                      setFormError(null);
                      setPending({ business, kind: 'reactivate' });
                    }}
                  >
                    {tr("Reativar")}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Modal
        open={pending !== null}
        onClose={() => (busy ? undefined : setPending(null))}
        title={
          pending?.kind === 'suspend'
            ? tr('Suspender {name}', { name: pending.business.name })
            : pending?.kind === 'removeLicense'
              ? tr('Excluir licença de {name}', { name: pending.business.name })
              : tr('Reativar {name}', { name: pending?.business.name ?? '' })
        }
        footer={
          <>
            <Button variant="ghost" disabled={busy} onClick={() => setPending(null)}>{tr("Voltar")}</Button>
            <Button
              variant={pending?.kind === 'reactivate' ? 'primary' : 'danger'}
              loading={busy}
              disabled={busy}
              onClick={() => void runAction()}
            >
              {pending?.kind === 'suspend'
                ? tr('Confirmar suspensão')
                : pending?.kind === 'removeLicense'
                  ? tr('Excluir licença')
                  : tr('Confirmar reativação')}
            </Button>
          </>
        }
      >
        {pending ? (
          <div className="space-y-4">
            {pending.kind === 'suspend' ? (
              <>
                <Alert tone="danger" title={tr("O que acontece agora")}>
                  {tr("O estabelecimento passa a")} <strong>{tr("Suspenso")}</strong>{tr(", os clientes não conseguem criar novas reservas e a assinatura em teste, ativa ou com pagamento pendente é movida para")} <strong>{tr("Suspenso")}</strong>{tr(". Um evento de assinatura é gravado com o motivo e a ação aparece em Auditoria (")}<code>{tr("admin.business_suspended")}</code>).
                </Alert>
                <div>
                  <label className="nl-label" htmlFor="suspend-reason">{tr("Motivo da suspensão")}</label>
                  <Textarea
                    id="suspend-reason"
                    rows={3}
                    maxLength={300}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={tr("Ex.: descumprimento dos termos de uso após notificação.")}
                  />
                  <p className="nl-hint mt-1">{tr("Obrigatório, 3 a 300 caracteres. Fica registrado no histórico da assinatura.")}</p>
                </div>
              </>
            ) : pending.kind === 'removeLicense' ? (
              <>
                <Alert tone="danger" title={tr("O que acontece agora")}>
                  {tr("A licença atual (")}<strong>{pending.business.planName ?? 'plano ativo'}</strong>{tr(") é removida do estabelecimento: ele volta a ficar")} <strong>{tr("sem plano")}</strong>{tr(", perde o acesso ao painel e o perfil público deixa de receber novos agendamentos. Se houver recorrência no Asaas, ela é cancelada antes. A conta, os agendamentos e o histórico de cobranças continuam.")}
                </Alert>
                <div>
                  <label className="nl-label" htmlFor="license-reason">{tr("Motivo (opcional)")}</label>
                  <Textarea
                    id="license-reason"
                    rows={2}
                    maxLength={300}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={tr("Ex.: licença concedida por engano em 26/10.")}
                  />
                  <p className="nl-hint mt-1">{tr("A remoção fica registrada em Auditoria (")}<code>{tr("admin.license_removed")}</code>).</p>
                </div>
              </>
            ) : (
              <Alert tone="warning" title={tr("O que acontece agora")}>
                {tr("O estabelecimento volta a operar, mas a assinatura suspensa retorna para")}{' '}
                <strong>{tr("Pagamento pendente")}</strong> {tr("— o acesso operacional completo só é liberado depois que você confirmar a cobrança. A ação é registrada em Auditoria (")}<code>{tr("admin.business_reactivated")}</code>).
              </Alert>
            )}
            {formError ? <Alert tone="danger" title={tr("Ação não concluída")}>{formError}</Alert> : null}
          </div>
        ) : null}
      </Modal>
    </AdminScreen>
  );
}
